import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { TargetIcon, ShieldCheckIcon, TrendingUpIcon, ArrowRightIcon } from 'lucide-react';
import { CTAButton } from '../ui/CTAButton';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.55,
      delay: i * 0.12,
      ease: 'easeOut'
    }
  })
};
const engines = [{
  icon: TargetIcon,
  label: 'SOURCING',
  title: 'Recruiting',
  desc: 'We launch targeted campaigns across every channel. You get a flood of qualified applicants without lifting a finger.'
}, {
  icon: ShieldCheckIcon,
  label: 'ANCHORING',
  title: 'Retention',
  desc: 'Triple-screened candidates only. We filter out the noise so every interview on your calendar is worth your time.'
}, {
  icon: TrendingUpIcon,
  label: 'PRODUCTION',
  title: 'Results',
  desc: 'Reps who show up, stay, and sell. Our onboarding system sets them up to produce fast.'
}];
const stats = [{
  value: '20–50',
  label: 'Qualified interviews per month'
}, {
  value: '70%+',
  label: 'Interview-to-hire rate'
}, {
  value: '45%',
  label: 'Better retention than industry avg'
}];
export function HowItWorksSection() {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    amount: 0.15
  });
  return <section className="relative w-full bg-[#1A2B4A] dot-texture py-24 px-6 overflow-hidden" data-id="element-314">
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-[#4ECDC4]/6 rounded-full blur-[100px] pointer-events-none" data-id="element-315" />

      <div ref={ref} className="max-w-5xl mx-auto relative z-10" data-id="element-316">
        <motion.div custom={0} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="text-center mb-16" data-id="element-317">
          <span className="inline-block bg-[#4ECDC4] text-[#1A2B4A] text-xs font-bold uppercase px-3 py-1 rounded-full mb-4 tracking-wider" data-id="element-318">
            The System
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-4 leading-tight" data-id="element-319">
            Three Engines. One Complete System.
          </h2>
          <p className="text-lg text-white/60 max-w-2xl mx-auto" data-id="element-320">
            Watch your pipeline come alive. Candidates flow through a
            structured, intentional process designed to deliver consistent,
            qualified hires.
          </p>
        </motion.div>

        {/* Three engines */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 relative" data-id="element-321">
          {engines.map((engine, i) => <motion.div key={engine.label} custom={i + 1} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="bg-white/10 backdrop-blur-sm border border-[#4ECDC4]/30 rounded-2xl p-6 flex flex-col items-center text-center relative" data-id="element-322">
              {/* Arrow connector — only between cards, not on last */}
              {i < engines.length - 1 && <div className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 z-20 items-center justify-center w-6 h-6 rounded-full bg-[#1A2B4A] border border-[#4ECDC4]/40" data-id="element-323">
                  <span className="text-[#4ECDC4] text-xs font-bold leading-none" data-id="element-324">
                    →
                  </span>
                </div>}
              <div className="w-20 h-20 rounded-full border-2 border-[#4ECDC4] flex items-center justify-center mb-5 relative" style={{
            boxShadow: '0 0 24px rgba(78, 205, 196, 0.25)'
          }} data-id="element-325">
                <div className="w-16 h-16 rounded-full bg-[#1A2B4A] flex items-center justify-center" data-id="element-326">
                  <engine.icon className="w-7 h-7 text-[#4ECDC4]" data-id="element-327" />
                </div>
              </div>
              <span className="text-[10px] font-black text-[#4ECDC4] uppercase tracking-widest mb-1" data-id="element-328">
                {engine.label}
              </span>
              <h3 className="text-xl font-black text-white mb-3" data-id="element-329">
                {engine.title}
              </h3>
              <p className="text-sm text-white/60 leading-relaxed" data-id="element-330">
                {engine.desc}
              </p>
            </motion.div>)}
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-14" data-id="element-331">
          {stats.map((stat, i) => <motion.div key={stat.value} custom={i + 4} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="bg-white/10 backdrop-blur-sm border border-[#4ECDC4]/20 rounded-2xl p-5 text-center" data-id="element-332">
              <p className="text-3xl font-black text-[#4ECDC4] mb-1" data-id="element-333">
                {stat.value}
              </p>
              <p className="text-sm text-white/60" data-id="element-334">{stat.label}</p>
            </motion.div>)}
        </div>

        <motion.div custom={7} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="flex justify-center" data-id="element-335">
          <CTAButton text="See How Each Engine Works →" data-id="element-336" />
        </motion.div>
      </div>
    </section>;
}